import { Box, BoxProps, Divider, Flex, Text, useColorMode } from '@chakra-ui/react'
import { BadgeIndicator, BadgeIndicatorProps, Button, ButtonProps } from 'components'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { RoutePath } from '../utils/routing'
import { SideMenuItem } from '../utils/sideMenuUtils'

interface Props extends SideMenuItem {
  buttonProps?: ButtonProps
  isCollapsed?: boolean
}

export default function SideMenuButton({
  buttonProps,
  text,
  isCurrent,
  subItems,
  page,
  isCollapsed,
  LeftIcon,
  isDisabled,
  statusDot,
}: Props) {
  const { colorMode } = useColorMode()
  const { workspaceId } = useParams()
  const navigate = useNavigate()

  const _statusDot =
    statusDot || (isCurrent ? undefined : subItems?.find(item => item.statusDot)?.statusDot)
  const IconContainer =
    _statusDot && isCollapsed
      ? (p: BadgeIndicatorProps) => (
          <BadgeIndicator value badgeProps={{ bg: `${_statusDot}.400` }} {...p} />
        )
      : (p: BoxProps) => <Box lineHeight={1} {...p} />

  return (
    <Flex w="100%" direction="column">
      <Button
        bg={isCurrent && colorMode === 'dark' && !subItems ? 'rgba(255, 255, 255, 0.2)' : ''}
        color={isCurrent ? (colorMode === 'light' ? 'primary.500' : 'white') : ''}
        isDisabled={isDisabled}
        size="lg"
        px={!isCollapsed ? 5 : 0}
        iconSpacing={!isCollapsed ? 2 : 0}
        justifyContent={!isCollapsed ? 'start' : 'center'}
        palette={colorMode === 'light' ? 'primary' : undefined}
        borderRadius="sm"
        variant="ghost"
        leftIcon={
          LeftIcon ? (
            <IconContainer>
              <LeftIcon fontSize="lg" />
            </IconContainer>
          ) : undefined
        }
        {...buttonProps}
        onClick={e => {
          if (!workspaceId) return

          navigate({ pathname: generatePath(page, { workspaceId, id: null }) })
          buttonProps?.onClick?.(e)
        }}
      >
        {!isCollapsed && (
          <>
            <Text lineHeight={1} textStyle="bodySmall">
              {text}
            </Text>
            {_statusDot && (
              <Box ml={2} height="8px" width="8px" borderRadius="full" bg={`${_statusDot}.400`} />
            )}
          </>
        )}
      </Button>
      {isCurrent && !isCollapsed && (subItems?.length || false) && (
        <Flex px={7} gap={0} flex={1}>
          <Divider
            orientation="vertical"
            borderColor={colorMode === 'light' ? 'black' : 'white'}
            opacity={colorMode === 'light' ? 0.1 : 0.2}
          />
          <Flex direction="column" flex={1}>
            {subItems.map(({ isCurrent, isDisabled, text, page, statusDot }, index) => {
              return (
                <Button
                  key={index}
                  bg={
                    isCurrent
                      ? colorMode === 'light'
                        ? 'rgba(60, 75, 211, 0.1)'
                        : 'rgba(255, 255, 255, 0.2)'
                      : ''
                  }
                  variant="ghost"
                  ml={2}
                  size="sm"
                  borderRadius="sm"
                  color={isCurrent ? (colorMode === 'light' ? 'primary.500' : 'white') : ''}
                  isDisabled={isDisabled}
                  onClick={() => {
                    if (!workspaceId) return

                    const [basePath, hash] = page.split('#')
                    navigate({
                      pathname: generatePath(basePath as RoutePath, { workspaceId, id: null }),
                      hash,
                    })
                  }}
                >
                  <Text lineHeight={1} w="100%" textAlign="left">
                    {text}
                  </Text>
                  {statusDot && (
                    <Box
                      ml={2}
                      height="8px"
                      width="8px"
                      borderRadius="full"
                      bg={`${statusDot}.400`}
                    />
                  )}
                </Button>
              )
            })}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
